import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import {
  AlertTriangle, ArrowLeft, BarChart3, Gamepad2, History, Image as ImageIcon,
  Loader2, Lock, Megaphone, Palette, RotateCcw, Save, Trash2, Upload, Users
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useTeamStore } from '../store/useTeamStore'
import TeamAvatar from '../components/TeamAvatar'
import ConfirmModal from '../../../components/ConfirmModal'
import {
  canEditAnnouncement, canEditTeam, countGraphemes, hasEnoughAlphanumeric,
  isAcceptableContact, teamErrorText
} from '../utils/teamFormat'
import {
  accentColor, cardNickColor, cardPrimaryColor, cardSecondaryColor, COLOR_KEYS,
  contentBgColor, isValidHex, normalizeHex, teamPageBackground, TEAM_THEME
} from '../utils/teamColors'
import {
  BG_FITS, backgroundImageStyle, backgroundOverlayStyle,
  buildBackgroundPayload, fromBackground, prepareAvatarFile, prepareBackgroundFile
} from '../utils/teamImages'
import { CARD_CLASS, SECTION_VARIANT, SECTIONS_VARIANT } from '../utils/teamUi'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'
import { sendYandexGoal } from '../../../services/yandexMetrikaService'

const LIMITS = {
  name: 24,
  description: 500,
  recruit_text: 300,
  announcement: 500,
  contact: 200
}

const TEXT_KEYS = ['name', 'description', 'recruit_text', 'contact_chat', 'contact_channel']

const FLAGS = [
  { key: 'recruiting', labelKey: 'team.flagRecruiting', label: 'Набор открыт', hintKey: 'team.flagRecruitingHint', hint: 'Банда показывается в каталоге и принимает заявки' },
  { key: 'show_members', labelKey: 'team.flagShowMembers', label: 'Показывать состав', hintKey: 'team.flagShowMembersHint', hint: 'Чужие видят список участников' },
  { key: 'show_treasury', labelKey: 'team.flagShowTreasury', label: 'Показывать казну', hintKey: 'team.flagShowTreasuryHint', hint: 'Чужие видят остаток казны' },
  { key: 'show_contacts', labelKey: 'team.flagShowContacts', label: 'Показывать контакты', hintKey: 'team.flagShowContactsHint', hint: 'Чужие видят ссылки на чат и канал' },
  { key: 'show_stats', labelKey: 'team.flagShowStats', label: 'Показывать статистику', hintKey: 'team.flagShowStatsHint', hint: 'Чужие видят статистику игр' }
]

const fromColors = (colors) => ({
  nick: colors?.nick ?? null,
  bg: colors?.bg ?? null,
  bg2: colors?.bg2 ?? null,
  bg_angle: Number.isFinite(colors?.bg_angle) ? colors.bg_angle : 180,
  accent: colors?.accent ?? null,
  avatar_frame: colors?.avatar_frame ?? null,
  card_bg: colors?.card_bg ?? null,
  content_bg: colors?.content_bg ?? null,
  text_secondary: colors?.text_secondary ?? null
})

/**
 * Diff цветов для автосохранения: только изменённые ключи (null — валидный
 * сброс к теме). Незавершённый ввод вроде «#FF8» не отправляем.
 */
const buildColorsPayload = (draft, initial) => {
  const payload = {}
  COLOR_KEYS.forEach((key) => {
    const next = normalizeHex(draft[key])
    const prev = normalizeHex(initial[key])
    if (draft[key] === null || draft[key] === '') {
      if (prev !== null) payload[key] = null
    } else if (next && next !== prev) {
      payload[key] = next
    }
  })
  if (draft.bg_angle !== initial.bg_angle) payload.bg_angle = draft.bg_angle
  return Object.keys(payload).length > 0 ? payload : null
}

const Toggle = ({ checked }) => (
  <span className={`relative h-6 w-10 shrink-0 rounded-full transition-colors ${checked ? 'bg-accent-primary' : 'bg-white/10'}`}>
    <span className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : ''}`} />
  </span>
)

/**
 * Настройки банды (`/teams/settings`) — создателю и заместителю; объявление
 * лидера правит только создатель.
 *
 * Тексты и контакты сохраняются кнопкой (у названия кулдаун 5 минут, поэтому
 * автосейв здесь не годится), тумблеры — сразу при клике, цвета и настройки
 * фона — автосохранением с debounce, как в настройках профиля.
 * Лимиты длины считаются в графемах: эмодзи — один символ, как на бэкенде.
 */
const TeamSettings = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    team, isTeamLoading, teamError, isSavingSettings, isUploadingImage, isMutating,
    loadTeam, saveSettings, saveAnnouncement, saveColors,
    uploadAvatar, deleteAvatar, uploadBackground, saveBackgroundSettings, deleteBackground,
    startDelete
  } = useTeamStore()

  const [texts, setTexts] = useState(null)
  const [announcement, setAnnouncement] = useState('')
  const [draft, setDraft] = useState(null)
  const [bgDraft, setBgDraft] = useState(null)
  const [pendingFlag, setPendingFlag] = useState(null)
  const [isAvatarDeleteOpen, setIsAvatarDeleteOpen] = useState(false)
  const [isBgDeleteOpen, setIsBgDeleteOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  const avatarInputRef = useRef(null)
  const bgInputRef = useRef(null)
  // Что сохранено на сервере: пока картинки фона нет, настройки в ответе не
  // видны — бейзлайн ведём локально и обновляем после каждого успешного сейва
  const bgSavedRef = useRef(null)
  const latestRef = useRef(null)

  useEffect(() => {
    loadTeam('my')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Инициализация редакторов после загрузки банды
  useEffect(() => {
    if (team && !draft) {
      setDraft(fromColors(team.colors))
      setBgDraft(fromBackground(team.background))
      bgSavedRef.current = fromBackground(team.background)
      setTexts({
        name: team.name || '',
        description: team.description || '',
        recruit_text: team.recruit_text || '',
        contact_chat: team.contacts?.chat || '',
        contact_channel: team.contacts?.channel || ''
      })
      setAnnouncement(team.announcement || '')
    }
  }, [team, draft])

  useEffect(() => {
    latestRef.current = { draft, bgDraft, team, bgSavedRef }
  }, [draft, bgDraft, team])

  // Автосохранение цветов: один запрос после того, как пользователь закончил
  // ввод (колорпикер и слайдер угла генерируют поток onChange)
  useEffect(() => {
    if (!draft || !team || isSavingSettings) return

    const timer = setTimeout(async () => {
      const payload = buildColorsPayload(draft, fromColors(team.colors))
      if (!payload) return

      const res = await saveColors(payload)
      if (res.ok) {
        sendYandexGoal('team-colors-save')
      } else {
        toast.error(teamErrorText(res.error, t, res))
      }
    }, 700)

    return () => clearTimeout(timer)
  }, [draft, team, isSavingSettings, saveColors, t])

  // Автосохранение настроек фона — отдельная JSON-ручка без перезаливки файла
  useEffect(() => {
    if (!bgDraft || !team || isSavingSettings) return

    const timer = setTimeout(async () => {
      const payload = buildBackgroundPayload(bgDraft, bgSavedRef.current)
      if (!payload) return

      const res = await saveBackgroundSettings(payload)
      if (res.ok) {
        bgSavedRef.current = { ...bgSavedRef.current, ...payload }
        sendYandexGoal('team-bg-settings-save')
      } else {
        toast.error(teamErrorText(res.error, t, res))
      }
    }, 700)

    return () => clearTimeout(timer)
  }, [bgDraft, team, isSavingSettings, saveBackgroundSettings, t])

  // Уход с экрана до срабатывания debounce — досохраняем не отправленное
  useEffect(() => () => {
    const latest = latestRef.current
    if (latest?.draft && latest.team) {
      const payload = buildColorsPayload(latest.draft, fromColors(latest.team.colors))
      if (payload) saveColors(payload)
    }
    if (latest?.bgDraft && latest.team) {
      const payload = buildBackgroundPayload(latest.bgDraft, latest.bgSavedRef.current)
      if (payload) saveBackgroundSettings(payload)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isTeamLoading || (!team && !teamError)) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 size={24} className="animate-spin text-accent-primary" />
      </div>
    )
  }

  // Настройки — только создателю и заместителю активной банды
  if (!team || !canEditTeam(team.my_role)) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col items-center px-4 py-16 text-center">
        <span className="mb-4 flex h-20 w-20 items-center justify-center rounded-full border border-white/10 bg-surface">
          <Lock size={32} className="text-text-secondary" />
        </span>
        <p className="mb-6 text-sm text-text-secondary">
          {t('team.err.forbidden', 'Недостаточно прав')}
        </p>
        <button
          onClick={() => navigate('/teams')}
          className="rounded-xl border border-accent-primary/30 bg-accent-primary/10 px-6 py-3 font-medium text-accent-primary transition-all active:scale-[0.98]"
        >
          {t('team.toCatalog', 'В каталог банд')}
        </button>
      </div>
    )
  }

  if (!draft || !texts) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 size={24} className="animate-spin text-accent-primary" />
      </div>
    )
  }

  const isOwner = team.my_role === 'owner'
  // Строго `=== true`: у не-создателя в delete_instant приходит null, а не false
  const isDeleteInstant = team.delete_instant === true
  const setKey = (key, value) => setDraft((d) => ({ ...d, [key]: value }))
  const setBgKey = (key, value) => setBgDraft((d) => ({ ...d, [key]: value }))
  const setText = (key, value) => setTexts((prev) => ({ ...prev, [key]: value }))

  const colorFields = [
    { key: 'nick', label: t('team.colorsNick', 'Цвет названия'), theme: TEAM_THEME.text },
    { key: 'text_secondary', label: t('team.colorsTextSecondary', 'Цвет подписей'), theme: TEAM_THEME.textSecondary },
    { key: 'accent', label: t('team.colorsAccent', 'Акцент'), theme: TEAM_THEME.accent },
    { key: 'avatar_frame', label: t('team.colorsAvatarFrame', 'Рамка аватара'), theme: TEAM_THEME.accent },
    { key: 'bg', label: t('team.colorsBg', 'Фон страницы'), theme: TEAM_THEME.bg },
    { key: 'bg2', label: t('team.colorsBg2', 'Второй цвет фона'), theme: TEAM_THEME.surface },
    // `card_bg` в настройках банды намеренно скрыт: карточек скинов у банды нет
    // (ключ красит только сетку скинов в профиле игрока). Из draft и из сброса
    // цветов он не выпилен — значение с бэкенда остаётся нетронутым
    { key: 'content_bg', label: t('team.colorsContentBg', 'Фон карточек'), theme: TEAM_THEME.surface }
  ]
  const previewBg = contentBgColor(draft)
  const previewPrimary = cardPrimaryColor(draft)
  const previewSecondary = cardSecondaryColor(draft)
  const previewAccent = accentColor(draft)
  const previewNick = cardNickColor(draft)
  const previewSurface = `${previewPrimary}0D`
  const previewBorder = `${previewPrimary}1F`

  // --- Тексты и контакты -----------------------------------------------------

  const nameLength = countGraphemes(texts.name)
  const isNameValid = nameLength >= 3 && nameLength <= LIMITS.name && hasEnoughAlphanumeric(texts.name)
  const isDescriptionValid = countGraphemes(texts.description) <= LIMITS.description
  const isRecruitValid = countGraphemes(texts.recruit_text) <= LIMITS.recruit_text
  const isChatValid = isAcceptableContact(texts.contact_chat)
  const isChannelValid = isAcceptableContact(texts.contact_channel)
  const canSaveTexts = isNameValid && isDescriptionValid && isRecruitValid && isChatValid && isChannelValid

  const serverTexts = {
    name: team.name || '',
    description: team.description || '',
    recruit_text: team.recruit_text || '',
    contact_chat: team.contacts?.chat || '',
    contact_channel: team.contacts?.channel || ''
  }

  const textsPayload = TEXT_KEYS.reduce((acc, key) => {
    if ((texts[key] || '') !== serverTexts[key]) acc[key] = texts[key] || ''
    return acc
  }, {})
  const hasTextChanges = Object.keys(textsPayload).length > 0

  const handleSaveTexts = async () => {
    if (!canSaveTexts || !hasTextChanges || isSavingSettings) return
    hapticImpact('medium')

    const res = await saveSettings(textsPayload)
    if (res.ok) {
      hapticNotify('success')
      sendYandexGoal('team-settings-save')
      toast.success(t('team.settingsSaved', 'Сохранено'))
    } else {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  // --- Тумблеры --------------------------------------------------------------

  // recruiting лежит на верхнем уровне карточки, остальные флаги — в team.visibility
  const flagValue = (key) => (key === 'recruiting' ? team.recruiting : team.visibility?.[key]) === true

  const handleFlag = async (key) => {
    if (pendingFlag || isSavingSettings) return
    hapticImpact('light')
    setPendingFlag(key)
    const res = await saveSettings({ [key]: !flagValue(key) })
    setPendingFlag(null)

    if (!res.ok) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  // --- Объявление ------------------------------------------------------------

  const announcementLength = countGraphemes(announcement)
  const hasAnnouncementChanges = announcement !== (team.announcement || '')

  const handleSaveAnnouncement = async () => {
    if (announcementLength > LIMITS.announcement || !hasAnnouncementChanges) return
    hapticImpact('medium')

    const res = await saveAnnouncement(announcement)
    if (res.ok) {
      hapticNotify('success')
      sendYandexGoal('team-announcement-save')
      toast.success(t('team.settingsSaved', 'Сохранено'))
    } else {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  // --- Картинки --------------------------------------------------------------

  const handleAvatarFile = async (file) => {
    if (!file || isUploadingImage) return

    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      toast.error(t('team.err.invalidFormat', 'Подходят только JPG и PNG'))
      return
    }

    let prepared
    try {
      prepared = await prepareAvatarFile(file)
    } catch (e) {
      toast.error(teamErrorText(e?.code, t))
      return
    }

    const res = await uploadAvatar(prepared)
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('team-avatar-upload')
    } else if (res) {
      // 413 от nginx приходит без JSON — разбираем по статусу
      toast.error(teamErrorText(res.status === 413 ? 'file_too_large' : res.error, t, res))
    }

    if (avatarInputRef.current) avatarInputRef.current.value = ''
  }

  const handleAvatarDelete = async () => {
    const res = await deleteAvatar()
    if (res?.ok) {
      setIsAvatarDeleteOpen(false)
      hapticNotify('success')
      sendYandexGoal('team-avatar-delete')
    } else if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  const handleBgFile = async (file) => {
    if (!file || isUploadingImage) return

    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      toast.error(t('team.err.invalidFormat', 'Подходят только JPG и PNG'))
      return
    }

    let prepared
    try {
      prepared = await prepareBackgroundFile(file)
    } catch (e) {
      toast.error(teamErrorText(e?.code, t))
      return
    }

    const res = await uploadBackground(prepared, bgDraft)
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('team-bg-upload')
      const fresh = fromBackground(res.background)
      setBgDraft(fresh)
      bgSavedRef.current = fresh
    } else if (res) {
      toast.error(teamErrorText(res.status === 413 ? 'file_too_large' : res.error, t, res))
    }

    if (bgInputRef.current) bgInputRef.current.value = ''
  }

  const handleBgDelete = async () => {
    const res = await deleteBackground()
    if (res?.ok) {
      setIsBgDeleteOpen(false)
      hapticNotify('success')
      sendYandexGoal('team-bg-delete')
      // Снятие фона сбрасывает и настройки отображения — редактор тоже
      const fresh = fromBackground(null)
      setBgDraft(fresh)
      bgSavedRef.current = fresh
    } else if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  // Две концовки одного запроса: пустую банду бэкенд распускает сразу
  // (disbanded → своей банды больше нет, уводим в каталог), иначе запускается
  // таймер на 6 часов и остаётся живая карточка с кнопкой отмены
  const handleDelete = async () => {
    const res = await startDelete()
    if (res?.ok) {
      hapticNotify('warning')
      setIsDeleteOpen(false)
      if (res.disbanded) {
        sendYandexGoal('team-delete-instant')
        toast.success(t('team.disbanded', 'Банда распущена'))
        navigate('/teams', { replace: true })
        return
      }
      sendYandexGoal('team-delete-start')
      navigate('/teams/my')
    } else if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  const bgPreviewUrl = typeof team.background?.url === 'string' ? team.background.url : null

  return (
    <div className="mx-auto w-full px-4 py-6 lg:max-w-3xl lg:px-6 lg:py-8">
      <motion.div variants={SECTIONS_VARIANT} initial="hidden" animate="visible" className="space-y-5">
        <motion.div variants={SECTION_VARIANT} className="flex items-center gap-3">
          <button
            onClick={() => navigate('/teams/my')}
            className="rounded-lg bg-surface p-2 text-white transition-all active:scale-[0.98]"
            aria-label={t('common.back', 'Назад')}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl text-white">{t('team.settings', 'Настройки банды')}</h1>
        </motion.div>

        {/* Аватар */}
        <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-4`}>
          <h2 className="mb-3 text-sm font-bold not-italic uppercase tracking-wide text-text-secondary">
            {t('team.avatar', 'Аватар банды')}
          </h2>
          <div className="flex items-center gap-4">
            <TeamAvatar team={team} size={72} />
            <div className="min-w-0 flex-1 space-y-2">
              <button
                onClick={() => avatarInputRef.current?.click()}
                disabled={isUploadingImage}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-accent-primary/30 bg-accent-primary/10 px-4 py-2.5 text-sm font-semibold text-accent-primary transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {isUploadingImage ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                {t('team.avatarUpload', 'Загрузить')}
              </button>
              {team.ava && (
                <button
                  onClick={() => setIsAvatarDeleteOpen(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-medium text-text-secondary transition-all active:scale-[0.98]"
                >
                  <Trash2 size={15} />
                  {t('team.avatarDelete', 'Убрать')}
                </button>
              )}
            </div>
          </div>
          <p className="mt-3 text-xs text-text-secondary">
            {t('team.avatarHint', 'JPG или PNG до 1 МБ. Рекомендуется квадратное изображение размером от 64 px.')}
          </p>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/jpeg,image/png"
            className="hidden"
            onChange={(e) => handleAvatarFile(e.target.files?.[0])}
          />
        </motion.div>

        {/* Тексты и контакты */}
        <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} space-y-4 p-4`}>
          <h2 className="text-sm font-bold not-italic uppercase tracking-wide text-text-secondary">
            {t('team.texts', 'Название и описание')}
          </h2>

          <div>
            <label className="mb-1.5 flex items-center justify-between text-xs text-text-secondary">
              <span>{t('team.name', 'Название банды')}</span>
              <span className={`tabular-nums ${nameLength > LIMITS.name ? 'text-red-400' : ''}`}>
                {nameLength}/{LIMITS.name}
              </span>
            </label>
            <input
              value={texts.name}
              onChange={(e) => setText('name', e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-dark-primary px-4 py-3 text-sm text-white focus:border-accent-primary focus:outline-none"
            />
            {texts.name && !isNameValid && (
              <p className="mt-1 text-xs text-red-400">
                {nameLength < 3 || nameLength > LIMITS.name
                  ? t('team.nameLengthHint', 'От 3 до 24 символов')
                  : t('team.namePlainHint', 'Нужно минимум 3 буквы или цифры')}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1.5 flex items-center justify-between text-xs text-text-secondary">
              <span>{t('team.description', 'Описание банды')}</span>
              <span className={`tabular-nums ${!isDescriptionValid ? 'text-red-400' : ''}`}>
                {countGraphemes(texts.description)}/{LIMITS.description}
              </span>
            </label>
            <textarea
              value={texts.description}
              onChange={(e) => setText('description', e.target.value)}
              rows={4}
              className="w-full resize-none rounded-xl border border-white/10 bg-dark-primary px-4 py-3 text-sm text-white focus:border-accent-primary focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1.5 flex items-center justify-between text-xs text-text-secondary">
              <span>{t('team.recruitText', 'Текст набора')}</span>
              <span className={`tabular-nums ${!isRecruitValid ? 'text-red-400' : ''}`}>
                {countGraphemes(texts.recruit_text)}/{LIMITS.recruit_text}
              </span>
            </label>
            <textarea
              value={texts.recruit_text}
              onChange={(e) => setText('recruit_text', e.target.value)}
              rows={3}
              placeholder={t('team.recruitPlaceholder', 'Ищем активных игроков')}
              className="w-full resize-none rounded-xl border border-white/10 bg-dark-primary px-4 py-3 text-sm text-white placeholder:text-text-secondary/60 focus:border-accent-primary focus:outline-none"
            />
            <p className="mt-1.5 text-xs text-text-secondary">
              {t('team.recruitHint', 'Этот текст видно в каталоге банд')}
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-xs text-text-secondary">
              {t('team.contactChatLabel', 'Ссылка на чат')}
            </label>
            <input
              value={texts.contact_chat}
              onChange={(e) => setText('contact_chat', e.target.value)}
              placeholder="https://t.me/..."
              className="w-full rounded-xl border border-white/10 bg-dark-primary px-4 py-3 text-sm text-white placeholder:text-text-secondary/60 focus:border-accent-primary focus:outline-none"
            />
            {!isChatValid && (
              <p className="mt-1 text-xs text-red-400">
                {t('team.contactHint', 'Только Telegram и Discord: t.me, discord.gg и т.п. Можно @username')}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-xs text-text-secondary">
              {t('team.contactChannelLabel', 'Ссылка на канал')}
            </label>
            <input
              value={texts.contact_channel}
              onChange={(e) => setText('contact_channel', e.target.value)}
              placeholder="https://t.me/..."
              className="w-full rounded-xl border border-white/10 bg-dark-primary px-4 py-3 text-sm text-white placeholder:text-text-secondary/60 focus:border-accent-primary focus:outline-none"
            />
            {!isChannelValid && (
              <p className="mt-1 text-xs text-red-400">
                {t('team.contactHint', 'Только Telegram и Discord: t.me, discord.gg и т.п. Можно @username')}
              </p>
            )}
          </div>

          <button
            onClick={handleSaveTexts}
            disabled={!canSaveTexts || !hasTextChanges || isSavingSettings}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary p-3.5 font-bold text-dark-primary transition-all active:scale-[0.98] disabled:opacity-50"
          >
            {isSavingSettings ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            {t('team.save', 'Сохранить')}
          </button>
        </motion.div>

        {/* Тумблеры видимости и набора */}
        <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-4`}>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-bold not-italic uppercase tracking-wide text-text-secondary">
            <Users size={14} className="text-accent-primary" />
            {t('team.visibility', 'Набор и видимость')}
          </h2>
          <div className="space-y-2">
            {FLAGS.map((flag) => (
              <button
                key={flag.key}
                type="button"
                role="switch"
                aria-checked={flagValue(flag.key)}
                onClick={() => handleFlag(flag.key)}
                disabled={pendingFlag != null}
                className="w-full rounded-xl border border-white/10 bg-dark-primary p-3.5 text-left transition-all active:scale-[0.98] disabled:opacity-60"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {pendingFlag === flag.key && (
                        <Loader2 size={14} className="animate-spin text-accent-primary" />
                      )}
                      <span className="text-sm font-medium text-white">{t(flag.labelKey, flag.label)}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-text-secondary">{t(flag.hintKey, flag.hint)}</p>
                  </div>
                  <Toggle checked={flagValue(flag.key)} />
                </div>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Объявление лидера — только создатель */}
        {canEditAnnouncement(team.my_role) && (
          <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-4`}>
            <h2 className="mb-3 flex items-center gap-2 text-sm font-bold not-italic uppercase tracking-wide text-text-secondary">
              <Megaphone size={14} className="text-accent-primary" />
              {t('team.announcement', 'Объявление лидера')}
            </h2>
            <label className="mb-1.5 flex items-center justify-between text-xs text-text-secondary">
              <span>{t('team.announcementHint', 'Видно только участникам банды')}</span>
              <span className={`tabular-nums ${announcementLength > LIMITS.announcement ? 'text-red-400' : ''}`}>
                {announcementLength}/{LIMITS.announcement}
              </span>
            </label>
            <textarea
              value={announcement}
              onChange={(e) => setAnnouncement(e.target.value)}
              rows={3}
              placeholder={t('team.announcementPlaceholder', 'Сбор в 11:00 для захвата режима Кейс-баттл')}
              className="w-full resize-none rounded-xl border border-white/10 bg-dark-primary px-4 py-3 text-sm text-white placeholder:text-text-secondary/60 focus:border-accent-primary focus:outline-none"
            />
            <button
              onClick={handleSaveAnnouncement}
              disabled={announcementLength > LIMITS.announcement || !hasAnnouncementChanges || isSavingSettings}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-accent-primary/30 bg-accent-primary/10 p-3 text-sm font-semibold text-accent-primary transition-all active:scale-[0.98] disabled:opacity-50"
            >
              {isSavingSettings ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {t('team.save', 'Сохранить')}
            </button>
          </motion.div>
        )}

        {/* Цвета */}
        <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-4`}>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-bold not-italic uppercase tracking-wide text-text-secondary">
            <Palette size={14} className="text-accent-primary" />
            {t('team.colors', 'Цвета банды')}
            {isSavingSettings && <Loader2 size={13} className="animate-spin text-accent-primary" />}
          </h2>

          {/* Живой предпросмотр */}
          <div className="mb-4 space-y-2 rounded-2xl p-3" style={{ background: teamPageBackground(draft) }}>
            <div
              className="relative overflow-hidden rounded-xl border p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
              style={{ backgroundColor: previewBg, borderColor: previewBorder }}
            >
              {bgPreviewUrl && bgDraft && (
                <>
                  <div className="pointer-events-none absolute" style={backgroundImageStyle({ ...bgDraft, url: bgPreviewUrl })} />
                  <div className="pointer-events-none absolute inset-0" style={backgroundOverlayStyle(bgDraft)} />
                </>
              )}
              <div className="relative flex flex-col items-center text-center">
                <TeamAvatar team={{ ...team, colors: draft }} size={48} />
                <p className="mt-2 max-w-full truncate font-bold" style={{ color: previewNick }}>
                  {texts.name || team.name}
                </p>
                <p className="mt-0.5 line-clamp-2 text-[10px]" style={{ color: previewSecondary }}>
                  {texts.description || t('team.description', 'Описание банды')}
                </p>
                <div className="mt-2 flex flex-wrap justify-center gap-1">
                  <span
                    className="rounded-full border px-2 py-0.5 text-[9px]"
                    style={{ color: previewSecondary, backgroundColor: previewSurface, borderColor: previewBorder }}
                  >
                    {t('team.previewMembers', '{{count}} из {{max}} участников', {
                      count: team.members_count,
                      max: team.max_members
                    })}
                  </span>
                  <span
                    className="rounded-full border px-2 py-0.5 text-[9px]"
                    style={{ color: previewSecondary, backgroundColor: previewSurface, borderColor: previewBorder }}
                  >
                    {t('team.onlineCount', '{{count}} онлайн', { count: team.online_count ?? 0 })}
                  </span>
                </div>
                <div className="mt-2 grid w-full grid-cols-4 gap-1">
                  {[
                    [Megaphone, t('team.contactChannel', 'Канал')],
                    [BarChart3, t('team.stats.short', 'Статистика')],
                    [Gamepad2, t('team.stats.gamesShort', 'Игры')],
                    [History, t('team.auditShort', 'История')]
                  ].map(([Icon, label]) => (
                    <span
                      key={label}
                      className="flex min-w-0 flex-col items-center gap-0.5 rounded-lg border px-1 py-1.5"
                      style={{ color: previewSecondary, backgroundColor: previewSurface, borderColor: previewBorder }}
                    >
                      <Icon size={12} style={{ color: previewAccent }} />
                      <span className="w-full truncate text-[8px] font-semibold">{label}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div
              className="rounded-xl border p-3"
              style={{ backgroundColor: previewBg, borderColor: `${previewAccent}66` }}
            >
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase" style={{ color: previewAccent }}>
                <Megaphone size={11} />
                {t('team.announcement', 'Объявление лидера')}
              </p>
              <p className="mt-1 line-clamp-2 text-[10px]" style={{ color: previewPrimary }}>
                {announcement || t('team.announcementPlaceholder', 'Сбор в 11:00 для захвата режима Кейс-баттл')}
              </p>
            </div>

            <div
              className="rounded-xl border p-3"
              style={{ backgroundColor: previewBg, borderColor: previewBorder }}
            >
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase" style={{ color: previewAccent }}>
                <Users size={11} />
                {t('team.members', 'Состав')}
              </p>
              <div
                className="mt-2 flex items-center gap-2 rounded-lg border p-2"
                style={{ backgroundColor: previewSurface, borderColor: previewBorder }}
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                  style={{ color: previewAccent, backgroundColor: `${previewAccent}1A` }}
                >
                  <Users size={13} />
                </span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="block truncate text-[10px] font-semibold" style={{ color: previewPrimary }}>
                    {t('team.roles.member', 'Участник')}
                  </span>
                  <span className="block text-[9px]" style={{ color: previewPrimary }}>
                    {t('publicProfile.online', 'Онлайн')}
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {colorFields.map((field) => {
              const value = isValidHex(draft[field.key]) ? draft[field.key] : field.theme
              const isCustom = isValidHex(draft[field.key])

              return (
                <div key={field.key} className="flex items-center gap-3 rounded-xl border border-white/5 bg-dark-primary p-2.5">
                  <input
                    type="color"
                    value={value}
                    onChange={(e) => setKey(field.key, e.target.value.toUpperCase())}
                    className="h-9 w-9 shrink-0 cursor-pointer rounded-lg border border-white/10 bg-transparent"
                    aria-label={field.label}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-white">{field.label}</p>
                    <p className="text-xs tabular-nums text-text-secondary">
                      {isCustom ? draft[field.key] : t('team.colorsDefault', 'по умолчанию')}
                    </p>
                  </div>
                  {isCustom && (
                    <button
                      onClick={() => setKey(field.key, null)}
                      className="shrink-0 rounded-lg border border-white/10 p-1.5 text-text-secondary transition-all active:scale-[0.98] hover:text-white"
                      aria-label={t('team.colorsReset', 'Сбросить')}
                    >
                      <RotateCcw size={13} />
                    </button>
                  )}
                </div>
              )
            })}
          </div>

          {/* Угол градиента активен только при заданном втором цвете */}
          {isValidHex(draft.bg2) && (
            <div className="mt-3">
              <label className="mb-1.5 flex items-center justify-between text-xs text-text-secondary">
                <span>{t('team.colorsAngle', 'Угол градиента фона')}</span>
                <span className="tabular-nums">{draft.bg_angle}°</span>
              </label>
              <input
                type="range"
                min={0}
                max={360}
                step={5}
                value={draft.bg_angle}
                onChange={(e) => setKey('bg_angle', Number(e.target.value))}
                className="w-full accent-accent-primary"
              />
            </div>
          )}

          <button
            onClick={() => setDraft({
              nick: null, bg: null, bg2: null, bg_angle: 180, accent: null,
              avatar_frame: null, card_bg: null, content_bg: null, text_secondary: null
            })}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 p-2.5 text-sm font-medium text-text-secondary transition-all active:scale-[0.98]"
          >
            <RotateCcw size={14} />
            {t('team.colorsResetAll', 'Сбросить все цвета')}
          </button>
        </motion.div>

        {/* Фон карточки банды */}
        <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-4`}>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-bold not-italic uppercase tracking-wide text-text-secondary">
            <ImageIcon size={14} className="text-accent-primary" />
            {t('team.background', 'Фон главной карточки')}
          </h2>

          <div className="flex gap-2">
            <button
              onClick={() => bgInputRef.current?.click()}
              disabled={isUploadingImage}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-accent-primary/30 bg-accent-primary/10 px-4 py-2.5 text-sm font-semibold text-accent-primary transition-all active:scale-[0.98] disabled:opacity-50"
            >
              {isUploadingImage ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
              {t('team.bgUpload', 'Загрузить фон')}
            </button>
            {bgPreviewUrl && (
              <button
                onClick={() => setIsBgDeleteOpen(true)}
                className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-medium text-text-secondary transition-all active:scale-[0.98]"
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
          <input
            ref={bgInputRef}
            type="file"
            accept="image/jpeg,image/png"
            className="hidden"
            onChange={(e) => handleBgFile(e.target.files?.[0])}
          />

          {bgPreviewUrl ? (
            <div className="mt-4 space-y-4">
              {/* Заполнение */}
              <div>
                <p className="mb-2 text-xs text-text-secondary">{t('team.bgFit', 'Заполнение')}</p>
                <div className="flex flex-wrap gap-2">
                  {BG_FITS.map((fit) => (
                    <button
                      key={fit}
                      onClick={() => setBgKey('fit', fit)}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-all active:scale-[0.98] ${
                        bgDraft.fit === fit
                          ? 'border-accent-primary bg-accent-primary/15 text-accent-primary'
                          : 'border-white/10 bg-white/5 text-text-secondary'
                      }`}
                    >
                      {t(`team.bgFits.${fit}`, fit)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Положение 3×3 */}
              <div>
                <p className="mb-2 text-xs text-text-secondary">{t('team.bgPosition', 'Положение')}</p>
                <div className="grid w-[108px] grid-cols-3 gap-1">
                  {[0, 50, 100].map((y) => (
                    [0, 50, 100].map((x) => {
                      const isActive = bgDraft.position_x === x && bgDraft.position_y === y
                      return (
                        <button
                          key={`${x}-${y}`}
                          onClick={() => setBgDraft((d) => ({ ...d, position_x: x, position_y: y }))}
                          className={`h-8 rounded-md border transition-all active:scale-[0.95] ${
                            isActive ? 'border-accent-primary bg-accent-primary/20' : 'border-white/10 bg-white/5'
                          }`}
                          aria-label={`${x}% ${y}%`}
                        />
                      )
                    })
                  ))}
                </div>
              </div>

              {/* Затемнение и размытие */}
              <div>
                <label className="mb-1.5 flex items-center justify-between text-xs text-text-secondary">
                  <span>{t('team.bgOverlay', 'Затемнение')}</span>
                  <span className="tabular-nums">{bgDraft.overlay}%</span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={bgDraft.overlay}
                  onChange={(e) => setBgKey('overlay', Number(e.target.value))}
                  className="w-full accent-accent-primary"
                />
              </div>

              <div>
                <label className="mb-1.5 flex items-center justify-between text-xs text-text-secondary">
                  <span>{t('team.bgBlur', 'Размытие')}</span>
                  <span className="tabular-nums">{bgDraft.blur} px</span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={20}
                  step={1}
                  value={bgDraft.blur}
                  onChange={(e) => setBgKey('blur', Number(e.target.value))}
                  className="w-full accent-accent-primary"
                />
              </div>
            </div>
          ) : (
            <p className="mt-3 text-xs text-text-secondary">
              {t('team.bgHint', 'JPG или PNG до 1 МБ, картинка ужимается до 500 px по длинной стороне.')}
            </p>
          )}
        </motion.div>

        {/* Роспуск банды — только создатель */}
        {isOwner && !team.delete && (
          <motion.div variants={SECTION_VARIANT} className="rounded-2xl border border-red-500/30 bg-red-500/5 p-4">
            <h2 className="mb-2 flex items-center gap-2 text-sm font-bold not-italic uppercase tracking-wide text-red-400">
              <AlertTriangle size={14} />
              {t('team.dangerZone', 'Роспуск банды')}
            </h2>
            <p className="mb-3 text-xs text-text-secondary">
              {isDeleteInstant
                ? t('team.deleteInstantHint', 'В банде никого кроме тебя и казна пуста — роспуск случится сразу, без таймера и без кулдауна на новую банду.')
                : t('team.deleteHint', 'Банда проживёт ещё 6 часов и будет заморожена — за это время удаление можно отменить. Остаток казны уйдёт создателю.')}
            </p>
            <button
              onClick={() => setIsDeleteOpen(true)}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm font-semibold text-red-400 transition-all active:scale-[0.98]"
            >
              <Trash2 size={16} />
              {t('team.delete', 'Распустить банду')}
            </button>
          </motion.div>
        )}
      </motion.div>

      <ConfirmModal
        isOpen={isAvatarDeleteOpen}
        onClose={() => setIsAvatarDeleteOpen(false)}
        onConfirm={handleAvatarDelete}
        isLoading={isSavingSettings}
        title={t('team.avatarDeleteTitle', 'Убрать аватар?')}
        message={t('team.avatarDeleteMessage', 'Банда вернётся к стандартной заглушке')}
        confirmText={t('team.avatarDelete', 'Убрать')}
      />

      <ConfirmModal
        isOpen={isBgDeleteOpen}
        onClose={() => setIsBgDeleteOpen(false)}
        onConfirm={handleBgDelete}
        isLoading={isSavingSettings}
        title={t('team.bgDeleteTitle', 'Убрать фон?')}
        message={t('team.bgDeleteMessage', 'Настройки отображения фона тоже сбросятся')}
        confirmText={t('team.avatarDelete', 'Убрать')}
      />

      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        isLoading={isMutating}
        title={t('team.deleteTitle', 'Распустить банду?')}
        message={isDeleteInstant
          ? t('team.deleteInstantMessage', 'Банда будет распущена сразу — отменить это уже нельзя. Сразу после роспуска можно вступить в другую банду или создать новую.')
          : t('team.deleteMessage', 'Банда проживёт ещё 6 часов и всё это время будет заморожена — за них удаление можно отменить. При роспуске остаток казны уйдёт тебе, а новую банду можно будет создать через час.')}
        confirmText={t('team.deleteConfirm', 'Распустить')}
      />
    </div>
  )
}

export default TeamSettings
